/**
 * Converts a (possibly null/empty) string lat/lon pair into a numeric location bias point.
 * Returns undefined when either coordinate is missing or not a finite number, so callers can
 * safely omit the bias instead of sending `{ lat: 0, lon: 0 }` (Number(null) === 0).
 */
export const toLocationBias = (coords: {
    lat?: string | null;
    lon?: string | null;
}): { lat: number; lon: number } | undefined => {
    if (coords.lat == null || coords.lat === '' || coords.lon == null || coords.lon === '') {
        return undefined;
    }
    const lat = Number(coords.lat);
    const lon = Number(coords.lon);
    return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : undefined;
};

/**
 * Checks if a point is inside a polygon using the ray casting algorithm.
 * @param point The point to check {lat, lon}
 * @param polygon The polygon coordinates array of {lat, lon}
 * @returns true if the point is inside the polygon
 */
export const isPointInPolygon = (
    point: { lat: number; lon: number },
    polygon: { lat: number; lon: number }[],
): boolean => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].lat,
            yi = polygon[i].lon;
        const xj = polygon[j].lat,
            yj = polygon[j].lon;

        const intersect =
            polygon[i].lon > point.lon !== polygon[j].lon > point.lon &&
            point.lat < ((xj - xi) * (point.lon - yi)) / (yj - yi) + xi;

        if (intersect) inside = !inside;
    }

    return inside;
};

type GeoJsonGeometry = {
    type?: string;
    coordinates?: unknown;
    radius?: number;
};

type GeoJsonInput = GeoJsonGeometry & {
    geometry?: GeoJsonGeometry;
    properties?: { radius?: number } | null;
};

const parseLatLon = (coord1: number, coord2: number): { lat: number; lon: number } => {
    const isLatFirst = coord1 > 40 && coord2 < 30;
    return {
        lat: isLatFirst ? coord1 : coord2,
        lon: isLatFirst ? coord2 : coord1,
    };
};

/**
 * Checks if a point is inside a GeoJSON geometry (Polygon or Point with radius).
 * @param point The point to check {lat, lon}
 * @param geoJson The GeoJSON object (can be Feature or Geometry)
 * @returns true if the point is inside the zone
 */
export const isPointInGeoJsonPolygon = (
    point: { lat: number; lon: number },
    geoJson: GeoJsonInput | null | undefined,
): boolean => {
    if (!geoJson) return false;

    // Handle Feature vs Geometry
    const geometry: GeoJsonGeometry | undefined = geoJson.type === 'Feature' ? geoJson.geometry : geoJson;
    const properties = geoJson.type === 'Feature' ? geoJson.properties : {};

    if (!geometry) return false;

    if (geometry.type === 'Polygon') {
        const coordinates = geometry.coordinates as number[][][] | undefined;
        if (!Array.isArray(coordinates) || !coordinates[0]) return false;

        const polygon = coordinates[0].map((coord: number[]) => {
            const { lat, lon } = parseLatLon(coord[0], coord[1]);
            return { lat, lon };
        });

        return isPointInPolygon(point, polygon);
    }

    if (geometry.type === 'Point' && (properties?.radius || geoJson.radius)) {
        const radius = (properties?.radius || geoJson.radius) as number;
        const [coord1, coord2] = geometry.coordinates as number[];

        const { lat: centerLat, lon: centerLon } = parseLatLon(coord1, coord2);

        const distance = getDistance(point.lat, point.lon, centerLat, centerLon);
        return distance <= radius;
    }

    return false;
};

/**
 * Returns corner points of a GeoJSON geometry in [lat, lon] format for map bounds calculation.
 */
export const getGeoJsonBoundsPoints = (geoJson: GeoJsonInput | null | undefined): [number, number][] => {
    if (!geoJson) {
        return [];
    }

    const geometry: GeoJsonGeometry | undefined = geoJson.type === 'Feature' ? geoJson.geometry : geoJson;
    const properties = geoJson.type === 'Feature' ? geoJson.properties : {};

    if (!geometry) {
        return [];
    }

    if (geometry.type === 'Polygon') {
        const coordinates = geometry.coordinates as number[][][] | undefined;
        if (!Array.isArray(coordinates) || !coordinates[0]) {
            return [];
        }

        return coordinates[0].map((coord) => {
            const { lat, lon } = parseLatLon(coord[0], coord[1]);
            return [lat, lon] as [number, number];
        });
    }

    if (geometry.type === 'Point') {
        const radius = (properties?.radius ?? geometry.radius ?? 1000) as number;
        const [coord1, coord2] = geometry.coordinates as number[];
        const { lat, lon } = parseLatLon(coord1, coord2);
        const latDelta = radius / 111_320;
        const lonDelta = radius / (111_320 * Math.cos((lat * Math.PI) / 180));

        return [
            [lat - latDelta, lon - lonDelta],
            [lat + latDelta, lon + lonDelta],
        ];
    }

    return [];
};

export const mergeLatLonBounds = (
    points: [number, number][],
): [[number, number], [number, number]] | null => {
    if (points.length === 0) {
        return null;
    }

    const lats = points.map(([lat]) => lat);
    const lons = points.map(([, lon]) => lon);

    return [
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)],
    ];
};

/**
 * Calculates distance between two points in meters using Haversine formula.
 */
function getDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
}

export type MaybeLatLon = { lat?: string | number | null; lon?: string | number | null };

/**
 * Return safely converted lat & lon coordinates
 */
export const getSafeCoords = (coords: MaybeLatLon | null | undefined) => {
    const lat = coords?.lat;
    const lon = coords?.lon;
    const safeLat = typeof lat === 'number' ? lat : parseFloat(lat ?? '');
    const safeLon = typeof lon === 'number' ? lon : parseFloat(lon ?? '');
    if (!Number.isFinite(safeLat) || !Number.isFinite(safeLon)) {
        return null;
    }
    return { lat: safeLat, lon: safeLon };
};
